import { useMutation, useQuery } from "@tanstack/react-query";
import { entityService } from "@/services/breeze/EntityService";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { getTriggerLabel } from "@/utils/workflow";
import { BasicTable } from "@/components/ui/BasicTable";
import { Button } from "@headlessui/react";
import { Play } from "lucide-react";
import { Workflow } from "@/types/breeze/Workflow";
import { workflowService } from "@/services/breeze/WorkflowService";
import { Box } from "@radix-ui/themes";
import { useConfirm } from "@/providers/ConfirmDialogProvider";

interface EntityWorkflowsProps {
  entityName: string;
  entityId: number;
}

export function EntityWorkflows({
  entityName,
  entityId,
}: EntityWorkflowsProps) {
  const confirm = useConfirm();

  const {
    data: workflows,
    isLoading: isWorkflowsLoading,
    error: workflowsError,
  } = useQuery({
    queryKey: ["entityWorkflows", entityName],
    queryFn: async () => {
      return entityService.getWorkflows(entityName);
    },
  });

  const { mutate: triggerWorkflow } = useMutation({
    mutationFn: async (workflow: Workflow) => {
      return workflowService.triggerWorkflow(workflow.id, {
        entity_name: entityName,
        entity_id: entityId,
        data: "",
      });
    },
  });

  const handleTriggerWorkflow = async (workflow: Workflow) => {
    const confirmed = await confirm({
      title: "Trigger Workflow",
      message: `Are you sure you want to trigger the workflow "${workflow.name}"?`,
      confirmText: "Trigger",
      cancelText: "Cancel",
    });

    if (confirmed) {
      triggerWorkflow(workflow);
    }
  };

  if (isWorkflowsLoading) return <div>Loading...</div>;
  if (workflowsError) return <div>Error loading workflows</div>;

  return (
    <Box className="pt-4 pb-3">
      <SectionHeader title="Workflows" />
      {!workflows || workflows.length === 0 ? (
        <div className="text-gray-500">No workflows found</div>
      ) : (
        <BasicTable
          columns={[
            { header: "Name", accessor: "name" },
            { header: "Description", accessor: "description" },
            {
              header: "Triggers",
              accessor: "triggers",
              cellRenderer: (workflow) =>
                workflow.triggers
                  .map((t) => getTriggerLabel(t.type))
                  .join(", "),
            },
            {
              header: "Status",
              accessor: "enabled",
              cellRenderer: (workflow) => (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    workflow.enabled
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {workflow.enabled ? "Enabled" : "Disabled"}
                </span>
              ),
            },
            {
              header: "Actions",
              cellRenderer: (workflow) => (
                <div>
                  <Button onClick={() => handleTriggerWorkflow(workflow)}>
                    <Play className="w-4 h-4 mr-2" />
                  </Button>
                </div>
              ),
            },
          ]}
          data={workflows}
        />
      )}
    </Box>
  );
}
